/**
 * useProductVariantPricingHistory — ViewModel for /products/variant-pricing-history.
 *
 * Same shape as useProductPricing was before its retirement: composes
 * usePaginatedList for the per-product pricing history (SOP 02
 * §"List Pagination") keyed by selectedProductId so picking a
 * different product resets back to page 1 automatically. Products
 * dropdown rides on every list response via `extras`.
 *
 * Read-only as of 2026-05-27 — setting the selling price moved to the
 * Variants screen. Owns:
 *   - products dropdown catalogue (extras on every fetch)
 *   - paginated pricing snapshots for the selected product
 */

import { useCallback, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES } from '../constants/routes.constants'
import type {
  ProductPickerEntry,
  ProductVariantPricingHistoryRowDto
} from '../models/product-variant-pricing-history.model'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

export type {
  ProductPickerEntry,
  ProductVariantPricingHistoryRowDto
} from '../models/product-variant-pricing-history.model'

export interface UseProductVariantPricingHistoryReturn {
  readonly products: ProductPickerEntry[]
  readonly selectedProductId: string | null
  readonly history: ProductVariantPricingHistoryRowDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly currentOnly: boolean
  readonly setCurrentOnly: (on: boolean) => void
  readonly selectProduct: (id: string | null) => void
  readonly setPage: (n: number) => void
  readonly reload: () => void
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly products: ProductPickerEntry[]
    readonly history: {
      readonly items: ProductVariantPricingHistoryRowDto[]
      readonly total: number
      readonly page: number
      readonly pageSize: number
    }
  }
}

export function useProductVariantPricingHistory(): UseProductVariantPricingHistoryReturn {
  const { authedFetch } = useAuth()
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [currentOnly, setCurrentOnly] = useState<boolean>(false)

  const paged = usePaginatedList<ProductVariantPricingHistoryRowDto, ProductPickerEntry[]>({
    deps: [selectedProductId, currentOnly],
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const params = new URLSearchParams()
        params.set('page', String(page))
        params.set('pageSize', String(pageSize))
        if (selectedProductId !== null) {
          params.set('product_id', selectedProductId)
        }
        if (currentOnly) {
          params.set('current', 'true')
        }
        const res = await authedFetch(
          `${APIROUTES.PRODUCTVARIANTPRICINGHISTORY}?${params.toString()}`,
          { signal }
        )
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.history.items,
          total: body.data.history.total,
          extras: body.data.products
        }
      },
      [authedFetch, selectedProductId, currentOnly]
    )
  })

  return {
    products: paged.extras ?? [],
    selectedProductId,
    history: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    currentOnly,
    setCurrentOnly,
    selectProduct: setSelectedProductId,
    setPage: paged.setPage,
    reload: paged.reload
  }
}
